import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";

import websocketService from "services/websocket";
import {
  ServerBasePrice,
  ServerCandlesPrice,
  ServerChartType,
} from "services/types";

interface UseHistoricalDataResult {
  data: ServerBasePrice[] | ServerCandlesPrice[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useHistoricalData = (
  symbol: string | null,
  timeframe: string,
  chartType: "line" | "candlestick" | "histogram",
): UseHistoricalDataResult => {
  const { isConnected } = useSelector((state: RootState) => state.websocket);
  const [data, setData] = useState<ServerBasePrice[] | ServerCandlesPrice[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dataRef = useRef<ServerBasePrice[] | ServerCandlesPrice[]>([]);

  // Маппинг типов для сервера
  const chartTypeMap: Record<string, ServerChartType> = {
    line: "line",
    candlestick: "candles",
    histogram: "bars",
  };

  const fetchData = async () => {
    if (!symbol) return;

    setIsLoading(true);
    setError(null);

    try {
      const serverChartType = chartTypeMap[chartType] || "line";

      const history = await websocketService.fetchHistoricalData(
        symbol,
        timeframe,
        serverChartType,
      );

      if (history && history.prices && isConnected) {
        setData(history.prices);
        dataRef.current = history.prices;
      } else {
        setData([]);
        dataRef.current = [];
      }

      setIsLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load historical data",
      );
      setIsLoading(false);
      console.error("Failed to load history:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [symbol, timeframe, chartType]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
};
